import { createSlice } from "@reduxjs/toolkit";
import { User, Meta } from "src/store/types";
import {
  doAssignStar,
  doBlockUser,
  doFetchReportedUsers,
  doFetchUserList,
  doFetchUserReports,
  doToggleSubscription,
  doUpdateUserRole,
} from "./user.action";

interface userState {
  users: User[];
  reportedUsers: User[];
  meta: Meta | null;
  reportedMeta: Meta | null;
  loading: boolean;
  reportedLoading: boolean;
  error: string | null;
  searchQuery: string;
  reportedSearchQuery: string;
  roleFilter: string;
  reportDetails: [];
  reportDetailsLoading: boolean;
  operationLoading: { [uid: string]: string }; // Track specific operations per user
}

const initialState: userState = {
  users: [] as User[],
  reportedUsers: [] as User[],
  meta: null,
  reportedMeta: null,
  loading: false,
  reportedLoading: false,
  error: null,
  searchQuery: "",
  roleFilter: "",
  reportedSearchQuery: "",
  reportDetails: [],
  reportDetailsLoading: false,
  operationLoading: {}, // Initialize as empty object
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    clearSearch: (state) => {
      state.searchQuery = "";
    },
    setReportedSearchQuery: (state, action) => {
      state.reportedSearchQuery = action.payload;
    },
    clearReportedSearch: (state) => {
      state.reportedSearchQuery = "";
    },
    setRoleFilter: (state, action) => {
      state.roleFilter = action.payload;
    },
    clearRoleFilter: (state) => {
      state.roleFilter = "";
    },
    clearOperationLoading: (state, action) => {
      const uid = action.payload;
      delete state.operationLoading[uid];
    },
  },
  extraReducers: (builder) => {
    // Fetch user list
    builder
      .addCase(doFetchUserList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doFetchUserList.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(doFetchUserList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetch reported posts
    builder
      .addCase(doFetchReportedUsers.pending, (state) => {
        state.reportedLoading = true;
        state.error = null;
      })
      .addCase(doFetchReportedUsers.fulfilled, (state, action) => {
        state.reportedLoading = false;
        state.reportedUsers = action.payload.data;
        state.reportedMeta = action.payload.meta;
      })
      .addCase(doFetchReportedUsers.rejected, (state, action) => {
        state.reportedLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(doFetchUserReports.pending, (state) => {
        state.reportDetailsLoading = true;
        state.error = null;
      })
      .addCase(doFetchUserReports.fulfilled, (state, action) => {
        state.reportDetailsLoading = false;
        state.reportDetails = action.payload.reports || [];
      })
      .addCase(doFetchUserReports.rejected, (state, action) => {
        state.reportDetailsLoading = false;
        state.error = action.payload as string;
      });

    // Block/unblock user
    builder
      .addCase(doBlockUser.pending, (state, action) => {
        const uid = action.meta.arg;
        state.operationLoading[uid] = "block";
        state.error = null;
      })
      .addCase(doBlockUser.fulfilled, (state, action) => {
        const uid = action.payload.uid;
        delete state.operationLoading[uid];
        // Remove per-user update logic
      })
      .addCase(doBlockUser.rejected, (state, action) => {
        const uid = action.meta.arg;
        delete state.operationLoading[uid];
        state.error = action.payload as string;
      });

    // Update user role
    builder
      .addCase(doUpdateUserRole.pending, (state, action) => {
        const uid = action.meta.arg.uid;
        state.operationLoading[uid] = "role";
        state.error = null;
      })
      .addCase(doUpdateUserRole.fulfilled, (state, action) => {
        const uid = action.payload.uid;
        delete state.operationLoading[uid];
        // Remove per-user update logic
      })
      .addCase(doUpdateUserRole.rejected, (state, action) => {
        const uid = action.meta.arg.uid;
        delete state.operationLoading[uid];
        state.error = action.payload as string;
      });

    // Assign star
    builder
      .addCase(doAssignStar.pending, (state, action) => {
        const uid = action.meta.arg;
        state.operationLoading[uid] = "star";
        state.error = null;
      })
      .addCase(doAssignStar.fulfilled, (state, action) => {
        const uid = action.payload.uid;
        delete state.operationLoading[uid];
        // Remove per-user update logic
      })
      .addCase(doAssignStar.rejected, (state, action) => {
        const uid = action.meta.arg;
        delete state.operationLoading[uid];
        state.error = action.payload as string;
      });

    // Toggle subscription
    builder
      .addCase(doToggleSubscription.pending, (state, action) => {
        const uid = action.meta.arg;
        state.operationLoading[uid] = "subscription";
        state.error = null;
      })
      .addCase(doToggleSubscription.fulfilled, (state, action) => {
        const uid = action.meta.arg;
        delete state.operationLoading[uid];
        // Remove per-user update logic
      })
      .addCase(doToggleSubscription.rejected, (state, action) => {
        const uid = action.meta.arg;
        delete state.operationLoading[uid];
        console.error("Toggle subscription failed:", action.payload);
      });
  },
});

// Export actions
export const {
  setSearchQuery,
  clearSearch,
  setReportedSearchQuery,
  clearReportedSearch,
  setRoleFilter,
  clearRoleFilter,
  clearOperationLoading,
} = userSlice.actions;

export default userSlice.reducer;
